import { useEffect, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import { parseCsv } from '../utils/csv';
import Pagination, { usePagination } from '../components/Pagination';
import { phoneChars, resultRowEmail, todayISO } from '../utils/format';
import { COLUMN_HEADINGS, withHeadings, fromHeadings } from '../utils/productColumns';
// utils/xlsx.js pulls in exceljs (a genuinely large library) purely to
// build the one .xlsx template below — dynamically imported so it's its
// own chunk, only fetched when someone actually downloads it, same
// reasoning as IntakeView's BulkUploadPanel.
const loadXlsxUtils = () => import('../utils/xlsx');

const EMPTY_USER = { first_name: '', last_name: '', email: '', phone: '', role: 'employer_admin', company_id: '', job_title: '' };
const ROLE_FILTERS = ['all', 'icib_admin', 'employer_admin'];

// Compare loosely (case/whitespace/hyphen-insensitive) rather than an
// exact string match — the role filter otherwise silently shows nothing
// for a role whose backend value doesn't come back byte-for-byte as our
// 'employer_admin'/'icib_admin' constants (e.g. "Employer Admin").
function normalizeRole(role) {
  return (role || '').toString().trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/* ICIB-08 — Manage user accounts and group assignments.
   GET /users (ICIB-08l) is fetched lazily here — via loadUsers, on mount —
   rather than riding along in IcibAdminContext's shared global refresh,
   since this is the only view that reads it. */
function UsersView() {
  const {
    db, status, createUser, deactivateUser, reactivateUser, resendSetup, resendAllSetup, rateLimited, bulkUploadEmployerAdmins,
    users: allUsers, usersStatus, usersError, loadUsers,
  } = useIcibAdmin();
  const { openDrawer, closeDrawer } = useDrawer();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_USER);
  const [busy, setBusy] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [sweeping, setSweeping] = useState(false);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const users = roleFilter === 'all' ? allUsers : allUsers.filter((u) => normalizeRole(u.role) === roleFilter);
  const { page, setPage, pageCount, pageItems: pagedUsers, total } = usePagination(users);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, company_id: form.role === 'employer_admin' ? form.company_id : undefined, job_title: form.role === 'employer_admin' ? (form.job_title || undefined) : undefined };
    const res = await createUser(body);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not create this user.', 'bad', res.error); return; }
    toast(`${res.first_name} ${res.last_name} created — account setup email queued.`, 'ok');
    setForm(EMPTY_USER);
  }

  async function onDeactivate(u) {
    if (!window.confirm(`Deactivate ${u.first_name} ${u.last_name}? This also disables their Entra sign-in.`)) return;
    const res = await deactivateUser(u.id);
    if (res?.error) toast(res.message || 'Could not deactivate this user.', 'bad', res.error);
    else { toast('User deactivated.', 'ok'); closeDrawer(); }
  }

  // ICIB-08n — undoes onDeactivate above.
  async function onReactivate(u) {
    const res = await reactivateUser(u.id);
    if (res?.error) toast(res.message || 'Could not reactivate this user.', 'bad', res.error);
    else { toast(`${u.first_name} ${u.last_name} reactivated.`, 'ok'); closeDrawer(); }
  }

  async function onResendSetup(u) {
    const res = await resendSetup(u.id);
    if (res?.error) toast(res.message || 'Could not resend the setup email.', 'bad', res.error);
    else toast(`Setup email resent to ${res.email}.`, 'ok');
  }

  async function onResendAllSetup() {
    if (rateLimited()) { toast('Too many resend requests. Limit is 10 per minute.', 'bad', 'rate_limited'); return; }
    setSweeping(true);
    const res = await resendAllSetup();
    setSweeping(false);
    if (res?.error) { toast(res.message || 'Could not resend setup emails.', 'bad', res.error); return; }
    const failedRows = (res.results || []).filter((r) => r.status === 'error');
    toast(`${res.resent_count || 0} setup email(s) queued${failedRows.length ? `, ${failedRows.length} skipped` : ''}.`, failedRows.length ? 'bad' : 'ok');
  }

  function open(u) {
    openDrawer(`${u.first_name} ${u.last_name}`, `ICIB-08b · ${u.role}`, (
      <UserDrawer initial={u} db={db} onDeactivate={onDeactivate} onReactivate={onReactivate} onResendSetup={onResendSetup} />
    ));
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Access</div>
      <div className="page-h"><h2>User accounts</h2></div>
      <p className="page-sub">Who can sign in, and which employers they can see.</p>

      {usersError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load the user list</b>{usersError}</div>}

      <div className="grid g2">
        <div>
          <div className="filterbar" style={{ marginTop: 0 }}>
            {ROLE_FILTERS.map((r) => (
              <span key={r} className="chip" aria-pressed={roleFilter === r} onClick={() => setRoleFilter(r)} role="button" tabIndex={0}>
                {r === 'all' ? 'All' : r.replace('_', ' ')}
              </span>
            ))}
            <span className="spacer" style={{ flex: 1 }} />
            <button className="btn sm" onClick={onResendAllSetup} disabled={sweeping}>{sweeping ? 'Sweeping…' : 'Resend all setup emails'}</button>
          </div>
          <div className="card">
            {usersStatus === 'loading' || usersStatus === 'idle' ? (
              <div className="empty"><b>Loading users…</b></div>
            ) : users.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Role</th><th>Companies</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {pagedUsers.map((u) => (
                      <tr className="clickable" key={u.id} onClick={() => open(u)}>
                        <td><span className="strong">{u.first_name} {u.last_name}</span><br /><span className="mono dim">{u.email}</span></td>
                        <td><span className="tag navy">{normalizeRole(u.role).replace('_', ' ')}</span></td>
                        <td>{(u.assigned_companies || []).length
                          ? u.assigned_companies.map((a) => <span key={a.company_id} className="tag" title={a.job_title || undefined} style={{ marginRight: 4 }}>{a.company_code}</span>)
                          : <span className="dim">—</span>}</td>
                        <td><span className={'tag' + (u.is_active ? ' ok' : ' off')}>{u.is_active ? 'active' : 'inactive'}</span></td>
                        <td><button className="btn sm" onClick={(e) => { e.stopPropagation(); open(u); }}>Open</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="empty"><b>No users</b></div>}
            <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
          </div>
        </div>

        <div>
          <div className="sec-h" style={{ marginTop: 0 }}><h3>New user</h3><span className="spacer" /><span className="ep">POST /users</span></div>
          <div className="card card-b">
            <form onSubmit={submit}>
              <div className="grid g2">
                <div className="field">
                  <label>First name <span className="req">*</span></label>
                  <input required value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Last name <span className="req">*</span></label>
                  <input required value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
                </div>
              </div>
              <div className="field">
                <label>Email <span className="req">*</span></label>
                <input type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="field">
                <label>Phone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: phoneChars(e.target.value) }))} placeholder="021 555 0110" />
                <div className="hint">Optional.</div>
              </div>
              <div className="field">
                <label>Role <span className="req">*</span></label>
                <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value, company_id: '' }))}>
                  <option value="employer_admin">Employer admin</option>
                  <option value="icib_admin">ICIB admin</option>
                </select>
              </div>
              {form.role === 'employer_admin' && (
                <>
                  <div className="field">
                    <label>First company <span className="req">*</span></label>
                    <select required value={form.company_id} onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))}>
                      <option value="" disabled>Select a company…</option>
                      {db.groups.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.company_code}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>Job title</label>
                    <input value={form.job_title} onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))} placeholder="HR Manager" />
                    <div className="hint">Optional — this person's job title at the company above.</div>
                  </div>
                </>
              )}
              <div className="btn-row"><button type="submit" className="btn pri" disabled={busy}>{busy ? 'Creating…' : 'Create user'}</button></div>
            </form>
          </div>

          <div className="sec-h"><h3>Bulk-add employer admins</h3><span className="spacer" /><span className="ep">POST /users/employer-admins/bulk-upload</span></div>
          <div className="card card-b">
            <BulkAdminUpload
              db={db}
              upload={bulkUploadEmployerAdmins}
              toast={toast}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// The sheet's column is company_code, same as the API field. An older
// sheet headed group_code (the API's name before the company rename)
// still uploads — mapped across on parse.
function toApiRows(rows) {
  return rows.map(({ group_code, ...r }) => ({ ...r, company_code: r.company_code || group_code || '' }));
}

/* ICIB-08g/08h — .xlsx template download + bulk create/assign. Fixed
   file shape (email, first_name, last_name, phone, company_code, job_title —
   phone and job_title optional, §4.2) — one row per (person, group) pair,
   so the same email can appear on multiple rows to add someone to several
   groups at once. first_name/last_name/phone only populate a newly created
   User — an existing account's profile is never overwritten. job_title is
   only stored when that row's assignment is newly created — a re-upload
   never overwrites an existing title. All-or-nothing (README §2.6): the
   whole file is rejected together if any row fails. Built client-side
   (same as IntakeView's focused templates) rather than through ICIB-08g's
   own GET /users/employer-admins/template — that endpoint returns a fixed
   CSV, but every other template in this app is a client-built .xlsx now,
   and this one is simple enough not to need the round trip. */
function BulkAdminUpload({ db, upload, toast }) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  // A filled-in template can run to hundreds of rows — page the preview
  // rather than rendering the whole file at once. Upload still sends
  // every row, not just the visible page.
  const rowsPage = usePagination(rows);
  const [busy, setBusy] = useState(false);
  const [buildingXlsx, setBuildingXlsx] = useState(false);
  const [result, setResult] = useState(null);
  // The per-row log is as long as the file — paged like the preview.
  const resultPage = usePagination(result?.results || []);

  async function onDownload() {
    setBuildingXlsx(true);
    try {
      const { downloadXlsxTemplate } = await loadXlsxUtils();
      // EXTR-13 — headings and header colours follow ICIB's own
      // Additions/Removals sheet, same as the intake templates.
      await downloadXlsxTemplate({
        filename: 'employer_admins_template.xlsx',
        icibStyle: true,
        ...withHeadings({ headers: ['email', 'first_name', 'last_name', 'phone', 'company_code', 'job_title'] }, COLUMN_HEADINGS),
      });
    } finally {
      setBuildingXlsx(false);
    }
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    rowsPage.setPage(1);
    if (/\.xlsx$/i.test(file.name)) {
      loadXlsxUtils().then(({ parseXlsx }) => file.arrayBuffer().then(parseXlsx)).then((r) => setRows(toApiRows(fromHeadings(r, COLUMN_HEADINGS))));
    } else {
      file.text().then((text) => setRows(toApiRows(fromHeadings(parseCsv(text), COLUMN_HEADINGS))));
    }
  }

  async function onUpload() {
    if (!rows.length) return;
    setBusy(true);
    const res = await upload(fileName, rows);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not process this file.', 'bad', res.error); return; }
    setResult({ ...res, sentRows: rows });
    resultPage.setPage(1);
    if (res.committed) {
      toast(`${res.rows_processed} row(s) committed.`, 'ok');
      setFileName(''); setRows([]);
    } else {
      toast('Some rows failed — nothing was written. Fix and re-upload.', 'bad');
    }
  }

  return (
    <>
      <div className="btn-row" style={{ marginBottom: 12 }}>
        <button type="button" className="btn sm" onClick={onDownload} disabled={buildingXlsx}>{buildingXlsx ? 'Building…' : 'Download .xlsx template'}</button>
      </div>
      <div className="field">
        <label>Completed file</label>
        <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} />
        <div className="hint">Columns: Email address, First Name, Surname, Cellphone (optional), Company Code, Job Title (optional) — a CSV can use the field names (email, first_name, …) instead. The same email can appear on multiple rows to assign one person to several companies.</div>
      </div>
      {rows.length > 0 && (
        <>
          <div className="tbl-wrap" style={{ marginBottom: 10 }}>
            <table>
              <thead><tr><th>Email</th><th>Name</th><th>Phone</th><th>Company code</th><th>Job title</th></tr></thead>
              <tbody>
                {rowsPage.pageItems.map((r, i) => (
                  <tr key={i}>
                    <td className="mono">{r.email}</td>
                    <td>{[r.first_name, r.last_name].filter(Boolean).join(' ') || <span className="dim">—</span>}</td>
                    <td className="mono dim">{r.phone || '—'}</td>
                    <td className="mono">{r.company_code}{!db.groups.some((g) => g.company_code === r.company_code) && <span className="tag bad" style={{ marginLeft: 6 }}>unknown code</span>}</td>
                    <td className="mono dim">{r.job_title || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginBottom: 10 }}><Pagination {...rowsPage} /></div>
          <div className="btn-row"><button type="button" className="btn pri sm" disabled={busy} onClick={onUpload}>{busy ? 'Uploading…' : `Upload ${rows.length} row(s)`}</button></div>
        </>
      )}
      {result && (
        <div className={'note' + (result.committed ? ' ok' : ' bad')} style={{ marginTop: 12 }}>
          <b>{result.committed ? 'Committed' : 'Rejected — nothing written'}</b>
          {resultPage.pageItems.map((r) => (
            <div key={r.row} className="mono" style={{ fontSize: 11.5 }}>
              row {r.row}{resultRowEmail(result, r) && ` ${resultRowEmail(result, r)}`}: {r.status}{r.message ? ` — ${r.message}` : ''}
            </div>
          ))}
          <Pagination {...resultPage} />
        </div>
      )}
    </>
  );
}

// PATCH /users/{id} profile fields. dob/phone/address/gender are nullable
// on User (§2.1) — a cleared input is sent as null, never '' (an empty
// string isn't a valid date or gender, and would 400 the whole request).
const PROFILE_FIELDS = ['first_name', 'last_name', 'dob', 'phone', 'address', 'gender', 'role'];
const NULLABLE_FIELDS = ['dob', 'phone', 'address', 'gender'];

function profileOf(u) {
  return {
    first_name: u.first_name || '', last_name: u.last_name || '', dob: u.dob || '', phone: u.phone || '',
    address: u.address || '', gender: u.gender || '', role: normalizeRole(u.role),
  };
}

/* `initial` is the row the drawer was opened with — the drawer itself
   reads the live row from `users` (refetched after every ICIB-08
   mutation), so assigning/removing a company or saving shows up here
   without closing and reopening it. */
function UserDrawer({ initial, db, onDeactivate, onReactivate, onResendSetup }) {
  const { users, assignGroup, unassignGroup, updateUser, loadUser, loadUsers } = useIcibAdmin();
  const u = users.find((x) => x.id === initial.id) || initial;
  const toast = useToast();
  // ICIB-08b — confirm the account still exists when the drawer opens. On
  // 404 (deleted after the list loaded) show the server's message instead
  // of the stale row's actions, and reload the list so the row disappears.
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    let live = true;
    setLoadError(null);
    loadUser(initial.id).then((res) => {
      if (!live || !res?.error) return;
      setLoadError(res);
      if (res.error === 'not_found') loadUsers();
    });
    return () => { live = false; };
  }, [initial.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [groupToAdd, setGroupToAdd] = useState('');
  const [jobTitleToAdd, setJobTitleToAdd] = useState('');
  const [edit, setEdit] = useState(() => profileOf(u));
  // Only the job titles the admin has actually changed, keyed by
  // company_id — anything not in here shows the row's current title.
  const [titleEdits, setTitleEdits] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [sendingSetup, setSendingSetup] = useState(false);
  const assigned = u.assigned_companies || [];
  const available = db.groups.filter((g) => !assigned.some((a) => a.company_id === g.id));
  const isEmployerAdmin = normalizeRole(u.role) === 'employer_admin';

  const current = profileOf(u);
  const changedFields = PROFILE_FIELDS.filter((k) => edit[k] !== current[k]);
  // A role change to icib_admin deletes every company assignment and is
  // rejected (400 invalid_role_for_company_assignment) if job titles ride
  // along in the same PATCH (§4.2) — so pending title edits are dropped.
  const promotingToIcib = isEmployerAdmin && edit.role === 'icib_admin';
  const changedTitles = isEmployerAdmin && !promotingToIcib
    ? assigned.filter((a) => a.company_id in titleEdits && titleEdits[a.company_id] !== (a.job_title || ''))
    : [];
  const dirty = changedFields.length > 0 || changedTitles.length > 0;

  async function onResendSetupClick() {
    setSendingSetup(true);
    await onResendSetup(u);
    setSendingSetup(false);
  }

  /* ICIB-08c — profile fields and every changed job title go in one
     PATCH, saved all-or-nothing: if any part is rejected, nothing is
     written. Job titles ride in `assignments` (a list of
     {company_id, job_title}) rather than the single company_id +
     job_title pair, so several companies' titles save together. Only
     changed fields are sent. */
  async function saveEdit(e) {
    e.preventDefault();
    if (!dirty) return;
    const patch = {};
    changedFields.forEach((k) => { patch[k] = NULLABLE_FIELDS.includes(k) && !edit[k].trim() ? null : edit[k]; });
    if (changedTitles.length) patch.assignments = changedTitles.map((a) => ({ company_id: a.company_id, job_title: titleEdits[a.company_id] }));
    setSavingEdit(true);
    const res = await updateUser(u.id, patch);
    setSavingEdit(false);
    if (res?.error) { toast(res.message || 'Could not save these changes — nothing was saved.', 'bad', res.error); return; }
    setTitleEdits({});
    toast('User updated.', 'ok');
  }

  async function addGroup(e) {
    e.preventDefault();
    if (!groupToAdd) return;
    const res = await assignGroup(u.id, groupToAdd, jobTitleToAdd);
    if (res?.error) toast(res.message || 'Could not assign this company.', 'bad', res.error);
    else { toast('Company assigned.', 'ok'); setGroupToAdd(''); setJobTitleToAdd(''); }
  }

  async function removeGroup(gid) {
    const res = await unassignGroup(u.id, gid);
    if (res?.error) { toast(res.message || 'Could not remove this assignment.', 'bad', res.error); return; }
    setTitleEdits(({ [gid]: _, ...rest }) => rest);
    toast('Assignment removed.', 'ok');
  }

  if (loadError) {
    return <div className="note bad"><b>{loadError.error === 'not_found' ? 'This account no longer exists' : "Couldn't load this account"}</b>{loadError.message || 'Please reload the list.'}</div>;
  }

  return (
    <>
      <div className="field">
        <label>Status</label>
        <div><span className={'tag' + (u.is_active ? ' ok' : ' off')}>{u.is_active ? 'active' : 'inactive'}</span></div>
      </div>
      <div className="btn-row" style={{ marginBottom: 16 }}>
        {!u.entra_object_id && u.is_active && <button className="btn sm" disabled={sendingSetup} onClick={onResendSetupClick}>{sendingSetup ? 'Sending…' : 'Resend setup email'}</button>}
        {u.is_active
          ? <button className="btn sm danger" onClick={() => onDeactivate(u)}>Deactivate</button>
          : <button className="btn sm" onClick={() => onReactivate(u)}>Reactivate</button>}
      </div>

      <div className="sec-h" style={{ marginTop: 0 }}><h3>Details</h3><span className="spacer" /><span className="ep">PATCH /users/{'{id}'}</span></div>
      <div className="field"><label>Email</label><div className="mono">{u.email}</div></div>
      <form onSubmit={saveEdit}>
        <div className="grid g2">
          <div className="field"><label>First name <span className="req">*</span></label><input required value={edit.first_name} onChange={(e) => setEdit((f) => ({ ...f, first_name: e.target.value }))} /></div>
          <div className="field"><label>Last name <span className="req">*</span></label><input required value={edit.last_name} onChange={(e) => setEdit((f) => ({ ...f, last_name: e.target.value }))} /></div>
          <div className="field"><label>Date of birth</label><input type="date" max={todayISO()} value={edit.dob} onChange={(e) => setEdit((f) => ({ ...f, dob: e.target.value }))} /></div>
          <div className="field"><label>Phone</label><input type="tel" value={edit.phone} onChange={(e) => setEdit((f) => ({ ...f, phone: phoneChars(e.target.value) }))} /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Address</label><input value={edit.address} onChange={(e) => setEdit((f) => ({ ...f, address: e.target.value }))} /></div>
          <div className="field"><label>Gender</label>
            <select value={edit.gender} onChange={(e) => setEdit((f) => ({ ...f, gender: e.target.value }))}>
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div className="field"><label>Role</label>
            <select value={edit.role} onChange={(e) => setEdit((f) => ({ ...f, role: e.target.value }))}>
              <option value="employer_admin">Employer admin</option>
              <option value="icib_admin">ICIB admin</option>
            </select>
          </div>
        </div>

        {isEmployerAdmin && (
          <>
            <div className="sec-h"><h3>Assigned companies</h3></div>
            {assigned.length ? assigned.map((a) => (
              <AssignedGroupRow
                key={a.company_id}
                a={a}
                jobTitle={titleEdits[a.company_id] ?? (a.job_title || '')}
                onJobTitleChange={(jt) => setTitleEdits((t) => ({ ...t, [a.company_id]: jt }))}
                onRemove={() => removeGroup(a.company_id)}
              />
            )) : <div className="empty"><b>No companies assigned</b></div>}
          </>
        )}

        <div className="hint" style={{ margin: '10px 0' }}>
          Email isn't editable here — it's the Entra login-resolution key. Moving someone off icib_admin is rejected if it would leave fewer than 2 active ICIB admins.
          {isEmployerAdmin && !promotingToIcib && ' Details and job titles save together — if any part is rejected, nothing is saved.'}
          {promotingToIcib && assigned.length > 0 && <> <b>Saving as ICIB admin removes all {assigned.length} company assignment{assigned.length === 1 ? '' : 's'}</b> — ICIB admins aren't assigned to companies.</>}
        </div>
        <div className="btn-row"><button type="submit" className="btn pri sm" disabled={savingEdit || !dirty}>{savingEdit ? 'Saving…' : 'Save changes'}</button></div>
      </form>

      {isEmployerAdmin && available.length > 0 && (
        <>
          <div className="sec-h"><h3>Add a company</h3><span className="spacer" /><span className="ep">POST /users/{'{id}'}/assign-company</span></div>
          <form onSubmit={addGroup}>
            <div className="btn-row">
              <select value={groupToAdd} onChange={(e) => setGroupToAdd(e.target.value)} style={{ flex: 1 }}>
                <option value="" disabled>Select a company…</option>
                {available.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.company_code}</option>)}
              </select>
            </div>
            <div className="btn-row" style={{ marginTop: 6 }}>
              <input value={jobTitleToAdd} onChange={(e) => setJobTitleToAdd(e.target.value)} placeholder="Job title (optional)" maxLength={255} style={{ flex: 1 }} />
              <button type="submit" className="btn sm" disabled={!groupToAdd}>Assign</button>
            </div>
          </form>
        </>
      )}
    </>
  );
}

// One assigned company's row. The job title is edited in place and saved
// with the rest of the Details form (as one `assignments` entry), not on
// its own; Remove is its own call (DELETE .../assign-company/{company_id}).
function AssignedGroupRow({ a, jobTitle, onJobTitleChange, onRemove }) {
  const dirty = jobTitle !== (a.job_title || '');
  return (
    <div className="btn-row" style={{ justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap' }}>
      <span className="tag">{a.company_code}</span>
      <input value={jobTitle} onChange={(e) => onJobTitleChange(e.target.value)} placeholder="Job title" maxLength={255} style={{ flex: 1, minWidth: 120 }} />
      {dirty && <span className="tag warn">unsaved</span>}
      <button type="button" className="btn sm danger" onClick={onRemove}>Remove</button>
    </div>
  );
}

export default UsersView;
